import { scrollToTop } from "@faire/help-center-utils/lib/window";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { assignHelpCenterSearchAndNavImprovements } from "@faire/help-center-utils/settings/getSettingHelpCenterSearchAndNavImprovements";
import { ChatbotTooltip } from "@faire/help-center/components/ChatbotTooltip/ChatbotTooltip";
import { ContactStrategy } from "@faire/help-center/components/ContactStrategy/ContactStrategy";
import { ContactUsMainBanner } from "@faire/help-center/components/ContactUsBanner/ContactUsMainBanner";
import { Footer } from "@faire/help-center/components/PageWrapper/Footer/Footer";
import { SingleTopic } from "@faire/help-center/components/Section/SingleTopic";
import { useSectionStore } from "@faire/help-center/hooks/useSectionStore";
import { useTrackHelpCenterMainPagesView } from "@faire/help-center/hooks/useTrackHelpCenterMainPagesView";
import { markPageJsExecuted } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import {
  trackHelpCenterHomePageView,
  trackHelpCenterHomePageViewTraceRendered,
} from "@faire/web-api--source/events/helpCenter/view/homePage";
import React, { useEffect, useLayoutEffect } from "react";

import { EXPERIMENT } from "../eventEnum";

import { HomeHeader } from "./HomeHeader";
import { PopularGuides } from "./PopularGuides";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

export const HomePage: React.FC = () => {
  const retailerToken = getRetailerToken();
  const brandToken = getBrandToken();
  const { isLoading, dataUpdatedAt } = useSectionStore();

  useEffect(() => {
    scrollToTop();
    trackHelpCenterHomePageView(retailerToken ?? "", brandToken ?? "");
  }, [brandToken, retailerToken]);

  useLayoutEffect(() => {
    trackHelpCenterHomePageViewTraceRendered();
  }, []);

  useTrackHelpCenterMainPagesView(EXPERIMENT.CONTROL);

  useEffect(() => {
    assignHelpCenterSearchAndNavImprovements();
  }, []);

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "help_center-page_homepage",
    pageLoadKey: "help_center-page_homepage",
    isPageLoading: isLoading,
    numComponents: 3,
    enableTrackPerformanceBreakdown: "internal-tracking",
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    dataUpdatedAt,
  });

  return (
    <>
      <HomeHeader loadTimingRef={loadTimingRef} />
      <ContactStrategy />
      <SingleTopic loadTimingRef={loadTimingRef} />
      <PopularGuides loadTimingRef={loadTimingRef} />
      <ContactUsMainBanner borderPosition="top" />
      <Footer />
      <ChatbotTooltip />
    </>
  );
};
